<template>
  <v-dialog
    v-model="show"
    max-width="600"
    overlay-color="darktext"
    overlay-opacity="0.6"
    :persistent="loading ? true : false"
    disabled
  >
    <v-card class="pa-5">
      <v-card-title>
        {{ confirmationDialogTitle }}
      </v-card-title>
      <v-card-text :class="mobile() ? 'pa-0' : ''">
        {{ confirmationDialogText }}
        <v-form
          ref="form"
          v-model="valid"
          @keyup.enter="submitDialogBtn()"
          @submit.prevent
        >
          <v-text-field
            v-model="title"
            data-testid="dialog-input"
            solo
            elevation="6"
            class="rounded-xxl text-body-2 pt-4"
            :rules="titleRules"
            :placeholder="confirmationDialogInputPlaceholder"
            autofocus
          />
          <v-textarea
            v-model="description"
            data-testid="description-textarea"
            elevation="6"
            class="rounded-xxl text-body-2"
            auto-grow
            solo
            :placeholder="confirmationDialogTextAreaPlaceholder"
          /> 
          <p>
            {{ $t('SharingSettingsLabel') }}
          </p>
          <v-radio-group
            v-model="forumType"
            class="colored-options forum"
            data-testid="forum-type"
          >
            <v-radio
              key="3"
              value="3"
              color="accent"
              class="black--text pa-5 option rounded-xxl"
              data-testid="forum-type-option-3"
              active-class="selected"
            >
              <template v-slot:label>
                <div>
                  <span class="text-subtitle-1 font-weight-bold">{{ $t('addPeopleOpenForumThreadTitle') }}</span><br> 
                  <span class="text-subtitle-2 grey--text font-weight-regular">{{ $t('addPeopleOpenForumThreadText') }}</span> 
                </div>
              </template>
            </v-radio>
            <v-radio
              key="1"
              value="1"
              color="accent"
              class="black--text pa-5 option rounded-xxl"
              data-testid="forum-type-option-1"
              active-class="selected"
            >
              <template v-slot:label>
                <div>
                  <span class="text-subtitle-1 font-weight-bold">{{ $t('addPeoplePrivateForumThreadTitle') }}</span><br> 
                  <span class="text-subtitle-2 grey--text font-weight-regular">{{ $t('addPeoplePrivateForumThreadText') }}</span> 

                  <v-checkbox
                    v-model="includeAllUsers"
                    color="accent"
                    class="no-colored-options pa-0 mb-0"
                    :class="{ 'selected' : includeAllUsers}"
                    data-testid="include-all-checkbox"
                    hide-details
                  >
                    <template v-slot:label>
                        <span class="text-subtitle-2 grey--text font-weight-regular">{{ $t('addPeoplePrivateForumThreadEveryone') }}</span><br> 
                    </template>
                  </v-checkbox>
                  <v-autocomplete
                    v-model="selectedUsers"
                    :menu-props="{
                      rounded: 'xxl',
                      zIndex: '204',
                      maxWidth: $vuetify.breakpoint.name =='xs' ? '280px' : '480px',
                    }"
                    :items="items"
                    :loading="isLoading"
                    :search-input.sync="search"
                    :rules="!includeAllUsers ? autocompleteRules : []"
                    color="white"
                    hide-selected
                    item-text="searchTerm"
                    item-value="id"
                    :placeholder="$t('typeToSearch')"
                    return-object
                    multiple
                    chips
                    solo
                    class="rounded-xxl text-body-2 px-1 mt-3"
                    @blur="closeSearch"
                    z-index="10px"         
                    data-testid="users-input"
                    @change="change"
                    :disabled="includeAllUsers"
                    :style="$vuetify.breakpoint.name =='xs' ? 'max-width: 220px;': 'max-width: 400px;'"
                  >
                    <template v-slot:no-data>
                      <p class="mx-5 mt-4">
                        {{ $t('noDataText') }}
                      </p>
                    </template>
                    <template v-slot:selection="data">
                      <v-chip
                        v-if="data.item.type == 'users'"
                        v-bind="data.attrs"
                        close
                        data-testid="user-chip"
                        @click:close="remove(data.item)"
                      >
                        <v-avatar
                          rounded="lg"
                          left
                        >
                          <v-img
                            v-if="data.item.attributes.avatar_thumb == null"
                            src="../assets/user_images/profile-avatar.png"
                          />
                          <v-img
                            v-else
                            :src="data.item.attributes.avatar_thumb"
                          />
                        </v-avatar>
                        <span class="text-truncate">{{ data.item.attributes.first_name }} {{ data.item.attributes.last_name }}</span>
                      </v-chip>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-avatar
                        v-if="data.item.type == 'users'"
                        rounded="lg"
                      >
                        <img
                          v-if="data.item.attributes.avatar_thumb == null"
                          src="../assets/user_images/profile-avatar.png"
                        >
                        <img
                          v-else
                          :src="data.item.attributes.avatar_thumb"
                        >
                      </v-list-item-avatar>
                      <v-list-item-avatar
                        v-else
                        rounded="lg"
                      >
                        <img
                          v-if="data.item.attributes.avatar_thumb == null"
                          src="../assets/user_images/target-group-avatar.png"
                        >
                      </v-list-item-avatar>
                      <v-list-item-content v-if="data.item.type == 'users'">
                        <v-list-item-title>{{ data.item.attributes.first_name + ' ' + data.item.attributes.last_name }} </v-list-item-title>
                        <v-list-item-subtitle> {{ data.item.attributes.email }} </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          ref="cancel"
          data-testid="cancel-button"
          color="darktext"
          min-width="100"
          dark
          elevation="0"
          class="rounded-xxl text-none px-4 cancel"
          :disabled="loading"
          @click="show=false"
        >
          {{ confirmationDialogCancelBtn }}
        </v-btn>
        <v-btn
          ref="submit"
          data-testid="submit-button"
          type="submit"
          color="accent"
          min-width="100"
          dark
          elevation="0"
          class="rounded-xxl text-none px-4 submit"
          :loading="loading"
          @click="submitDialogBtn()"
        >
          {{ confirmationDialogSubmitBtn }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { ruleRequired, ruleValidMin2Max70Characters } from '../rules/formRules.js';
import { searchPeopleInActivity } from '../api/activityApi'
import i18n from '../plugins/i18n';
import { repeatedMethodsMixin } from '../mixins/repeatedMethodsMixin'

export default {
  mixins: [repeatedMethodsMixin],
  props: {
    visible: Boolean,
    loading: {
      type: Boolean,
      default: false
    }
  },
  data(){
      return{
        valid: true,
        titleRules: ruleRequired('labelTitle').concat(ruleValidMin2Max70Characters('labelTitle')),
       
        alertMsg: '',
        check: false,
        forumType: '3',
        title: '',
        description: '',
        confirmationDialogTitle: i18n.t('confirmationDialogTitleCreateActivityForumThread'), 
        confirmationDialogText: i18n.t('confirmationDialogTextCreateActivityForumThread'),
        confirmationDialogInputPlaceholder: i18n.t('confirmationDialogInputPlaceholderCreateActivityForumThread'),
        confirmationDialogTextAreaPlaceholder: i18n.t('confirmationDialogInputTextAreaPlaceholderCreateActivityForumThread'), 
        confirmationDialogSubmitBtn: i18n.t('create'),
        confirmationDialogCancelBtn: i18n.t('back'),
        selectedUsers: [],
        entries:[],
        search: null,
        isLoading: false,
        activityId: this.$route.params.id,
        //items: [],
        autocompleteRules: [
            v => {
                if (!v || v.length < 1 && this.forumType == 1){
                    return  i18n.t('labelMembers');
                }
                else return true;
            }
        ],
        errors: [],
        includeAllUsers: false
      }
    },  
    computed: {
        show: {
            get () {
                return this.visible
            },
            set (value) {
                if (!value) {
                    this.$emit('close')
                    this.$refs.form.reset()
                }
            }
        },
         items () {
            return this.entries.map(entry => {
                if (entry.type == 'users') {
                    const searchTerm = entry.attributes.first_name + ' ' + entry.attributes.last_name + ' ' + entry.attributes.email
                    return Object.assign({}, entry, { searchTerm })
                }
            })
        },
        selectedUsersEmails() {
            return this.selectedUsers.map(entry => {
                if (entry.type == 'users') {
                    if(entry.attributes.email){
                        return entry.attributes.email
                    }
                }
            })
        },
    },
        watch: {
        async search (val) {
            if (this.isLoading) return 

            var entriesUsers = []
            if(val && val.trim().length >= 2){
                this.isLoading = true
                try {
                    const response = await searchPeopleInActivity(this.activityId, val)
                    if(response.status == 200) {
                        // this.entries = response.data.data
                        entriesUsers = response.data.data
                        entriesUsers.forEach(function(item) {
                            item.sort_term = item.attributes.first_name.toLowerCase() + ' ' + item.attributes.last_name.toLowerCase() + ' ' + item.attributes.email.toLowerCase()
                        })
                    }            
                } catch (e) {
                    if(e.response){
                        this.alertValue = true
                        if(e.response.status == 403){
                            this.$router.push({name: 'PageAccessDenied'})
                        } else {
                            this.alertType = 'error'
                            this.alertMsg =  i18n.t('serverError')
                        }
                    }            
                }

                this.entries = entriesUsers
                this.isLoading = false
            }

            this.isLoading = false
        },
        includeAllUsers (val) {
          if(val){
            this.selectedUsers = []
          }
        }
    },
    created() {

    },
    methods: {
    change(){
            this.search = ''
        },
        remove(item) {
            this.selectedUsers.splice(this.selectedUsers.indexOf(item), 1)
            this.selectedUsers = [...this.selectedUsers]
        },

      submitDialogBtn() {
        this.valid = this.$refs.form.validate()
          if(this.valid && this.forumType){
            this.$emit('proceedSubmit',this.title, this.description, this.selectedUsersEmails, this.forumType, this.includeAllUsers)   
          }     
      },
      closeSearch(){
          this.search = ''
          this.entries = this.selectedUsers.slice();
        },
  },
}
</script>
<style>

.colored-options .v-input--selection-controls__input{
      align-self: baseline;
}
</style>